import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  IconButton,
  Collapse,
  InputAdornment,
  Divider,
} from "@mui/material";
import {
  Dns,
  Cloud,
  GitHub,
  Close,
  ExpandMore,
  ExpandLess,
  Visibility,
  VisibilityOff,
  ArrowBack,
  ChevronRight,
} from "@mui/icons-material";
import { createApplication } from "../../redux/slices/applicationSlice";

const deploymentTypes = [
  {
    type: "GIT_DOCKERFILE",
    title: "Git Repository",
    desc: "Build & deploy from a repo with a Dockerfile",
    icon: <GitHub sx={{ fontSize: 22 }} />,
    color: "#333",
  },
  {
    type: "DOCKER_IMAGE",
    title: "Docker Image",
    desc: "Deploy from Docker Hub or any registry",
    icon: <Cloud sx={{ fontSize: 22 }} />,
    color: "#2496ed",
  },
  {
    type: "WORDPRESS",
    title: "WordPress",
    desc: "Managed WordPress with database & admin",
    icon: <Dns sx={{ fontSize: 22 }} />,
    color: "#21759b",
  },
];

const NAME_REGEX = /^[a-zA-Z0-9][a-zA-Z0-9-_]*$/;

// Small uppercase section label — consistent with AddServiceDialog
const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={600}
    color="text.secondary"
    sx={{
      display: "block",
      mb: 1,
      textTransform: "uppercase",
      letterSpacing: 0.5,
    }}
  >
    {children}
  </Typography>
);

const CreateApplicationDialog = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { createLoading, error } = useSelector((state) => state.applications);

  const [step, setStep] = useState(0); // 0 = type, 1 = config
  const [selectedType, setSelectedType] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [nameError, setNameError] = useState("");
  const [form, setForm] = useState({
    name: "",
    description: "",
    // Git
    gitUrl: "",
    gitBranch: "main",
    dockerfilePath: "Dockerfile",
    gitAccessToken: "",
    internalPort: "3000",
    // Docker Image
    image: "",
    // WordPress
    wpEmail: "",
    wpTitle: "My WordPress Site",
    wpUsername: "admin",
    wpPassword: "",
    // Environment
    envVarsText: "",
  });

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "name") {
      if (!value) setNameError("");
      else if (!NAME_REGEX.test(value))
        setNameError(
          "Only letters, numbers, hyphens and underscores. Must start with a letter or number.",
        );
      else setNameError("");
    }
  };

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setStep(1);
  };

  const isValid = () => {
    if (!form.name || nameError) return false;
    if (selectedType === "GIT_DOCKERFILE" && !form.gitUrl) return false;
    if (selectedType === "DOCKER_IMAGE" && !form.image) return false;
    if (selectedType === "WORDPRESS" && !form.wpEmail) return false;
    return true;
  };

  const parseEnvVars = (text) => {
    if (!text.trim()) return undefined;
    const vars = [];
    text.split("\n").forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) return;
      const idx = trimmed.indexOf("=");
      if (idx > 0) {
        vars.push({
          key: trimmed.substring(0, idx).trim(),
          value: trimmed.substring(idx + 1).trim(),
        });
      }
    });
    return vars.length > 0 ? vars : undefined;
  };

  const handleCreate = async () => {
    const data = {
      name: form.name,
      description: form.description || undefined,
      deploymentType: selectedType,
    };
    const envVars = parseEnvVars(form.envVarsText);

    if (selectedType === "GIT_DOCKERFILE") {
      data.gitConfig = {
        repoUrl: form.gitUrl,
        branch: form.gitBranch || "main",
        dockerfilePath: form.dockerfilePath || "Dockerfile",
        accessToken: form.gitAccessToken || undefined,
        internalPort: parseInt(form.internalPort, 10) || 3000,
      };
      if (envVars) data.envVars = envVars;
    }

    if (selectedType === "DOCKER_IMAGE") {
      data.services = [
        {
          name: "main",
          image: form.image,
          serviceType: "WEB",
          internalPort: 80,
          ...(envVars && { envVars }),
        },
      ];
    }

    if (selectedType === "WORDPRESS") {
      data.wpConfig = {
        adminEmail: form.wpEmail,
        adminUser: form.wpUsername || "admin",
        adminPassword: form.wpPassword || undefined,
        siteTitle: form.wpTitle || "My WordPress Site",
      };
    }

    try {
      const result = await dispatch(createApplication(data)).unwrap();
      navigate(`/dashboard/applications/${result.id}`);
      handleClose();
    } catch (err) {
      // error surfaced via Redux state
    }
  };

  const handleClose = () => {
    setStep(0);
    setSelectedType(null);
    setShowAdvanced(false);
    setShowToken(false);
    setNameError("");
    setForm({
      name: "",
      description: "",
      gitUrl: "",
      gitBranch: "main",
      dockerfilePath: "Dockerfile",
      gitAccessToken: "",
      internalPort: "3000",
      image: "",
      wpEmail: "",
      wpTitle: "My WordPress Site",
      wpUsername: "admin",
      wpPassword: "",
      envVarsText: "",
    });
    onClose();
  };

  const selectedDT = deploymentTypes.find((d) => d.type === selectedType);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      {/* ── Title ─────────────────────────────────────────────────────────── */}
      <DialogTitle
        sx={{
          px: 3,
          py: 1.75,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {/* Back arrow on step 1 */}
          {step === 1 && (
            <IconButton
              size="small"
              onClick={() => {
                setStep(0);
                setSelectedType(null);
              }}
              sx={{ color: "text.secondary", mr: 0.5 }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {step === 0
                ? "New Application"
                : `Configure ${selectedDT?.title}`}
            </Typography>
            {step === 1 && (
              <Typography variant="caption" color="text.secondary">
                Step 2 of 2 — fill in the details below
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={handleClose}
            sx={{ color: "text.secondary" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 2.5 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: 2 }}>
            {typeof error === "string"
              ? error
              : error.error || "Something went wrong"}
          </Alert>
        )}

        {/* ── Step 0: Choose type ───────────────────────────────────────── */}
        {step === 0 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              What would you like to deploy?
            </Typography>

            {deploymentTypes.map((dt) => (
              <Box
                key={dt.type}
                onClick={() => handleTypeSelect(dt.type)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.75,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  cursor: "pointer",
                  transition: "all 0.15s",
                  "&:hover": {
                    borderColor: "primary.main",
                    bgcolor: "action.hover",
                  },
                }}
              >
                {/* Icon badge */}
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    bgcolor: "action.selected",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: dt.color,
                    flexShrink: 0,
                  }}
                >
                  {dt.icon}
                </Box>

                {/* Text */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" fontWeight={600}>
                    {dt.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {dt.desc}
                  </Typography>
                </Box>

                {/* Arrow hint */}
                <ChevronRight
                  sx={{ fontSize: 20, color: "text.disabled", flexShrink: 0 }}
                />
              </Box>
            ))}
          </Box>
        )}

        {/* ── Step 1: Configure ─────────────────────────────────────────── */}
        {step === 1 && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {/* App Name */}
            <Box>
              <SectionLabel>Application</SectionLabel>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <TextField
                  label="Application Name *"
                  placeholder="my-app"
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  error={!!nameError}
                  helperText={
                    nameError ||
                    "Letters, numbers, hyphens and underscores only"
                  }
                  fullWidth
                  size="small"
                  autoFocus
                />
                <TextField
                  label="Description (optional)"
                  placeholder="A short description of your app"
                  value={form.description}
                  onChange={(e) => setField("description", e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                />
              </Box>
            </Box>

            <Divider />

            {/* Git Config */}
            {selectedType === "GIT_DOCKERFILE" && (
              <Box>
                <SectionLabel>Git Configuration</SectionLabel>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <TextField
                    label="Repository URL *"
                    placeholder="https://github.com/user/repo.git"
                    value={form.gitUrl}
                    onChange={(e) => setField("gitUrl", e.target.value)}
                    fullWidth
                    size="small"
                  />
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                      label="Branch"
                      value={form.gitBranch}
                      onChange={(e) => setField("gitBranch", e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Dockerfile Path"
                      value={form.dockerfilePath}
                      onChange={(e) =>
                        setField("dockerfilePath", e.target.value)
                      }
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                  <TextField
                    label="Internal Port"
                    placeholder="3000"
                    value={form.internalPort}
                    onChange={(e) => setField("internalPort", e.target.value)}
                    size="small"
                    type="number"
                    helperText="Port your app listens on inside the container"
                    sx={{ maxWidth: 200 }}
                  />

                  {/* Private repo toggle */}
                  <Box
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      onClick={() => setShowAdvanced(!showAdvanced)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        py: 1.25,
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { bgcolor: "action.hover" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        fontWeight={500}
                        color="text.secondary"
                      >
                        Private repository
                      </Typography>
                      {showAdvanced ? (
                        <ExpandLess
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                        />
                      ) : (
                        <ExpandMore
                          fontSize="small"
                          sx={{ color: "text.disabled" }}
                        />
                      )}
                    </Box>
                    <Collapse in={showAdvanced}>
                      <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>
                        <TextField
                          label="Access Token"
                          placeholder="ghp_xxxxxxxxxxxx"
                          value={form.gitAccessToken}
                          onChange={(e) =>
                            setField("gitAccessToken", e.target.value)
                          }
                          fullWidth
                          size="small"
                          type={showToken ? "text" : "password"}
                          helperText="Required for private repositories"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={() => setShowToken(!showToken)}
                                  edge="end"
                                >
                                  {showToken ? (
                                    <VisibilityOff fontSize="small" />
                                  ) : (
                                    <Visibility fontSize="small" />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Box>
                    </Collapse>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Docker Image Config */}
            {selectedType === "DOCKER_IMAGE" && (
              <Box>
                <SectionLabel>Image Configuration</SectionLabel>
                <TextField
                  label="Docker Image *"
                  placeholder="nginx:alpine"
                  value={form.image}
                  onChange={(e) => setField("image", e.target.value)}
                  fullWidth
                  size="small"
                  helperText="e.g. nginx:alpine, redis:7, my-registry.io/myapp:latest"
                />
              </Box>
            )}

            {/* WordPress Config */}
            {selectedType === "WORDPRESS" && (
              <Box>
                <SectionLabel>WordPress Configuration</SectionLabel>
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}
                >
                  <TextField
                    label="Admin Email *"
                    placeholder="admin@example.com"
                    value={form.wpEmail}
                    onChange={(e) => setField("wpEmail", e.target.value)}
                    fullWidth
                    size="small"
                    type="email"
                  />
                  <Box sx={{ display: "flex", gap: 1.5 }}>
                    <TextField
                      label="Site Title"
                      value={form.wpTitle}
                      onChange={(e) => setField("wpTitle", e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <TextField
                      label="Admin Username"
                      value={form.wpUsername}
                      onChange={(e) => setField("wpUsername", e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {/* Environment Variables (Git & Docker Image) */}
            {(selectedType === "GIT_DOCKERFILE" ||
              selectedType === "DOCKER_IMAGE") && (
              <Box>
                <SectionLabel>Environment Variables (optional)</SectionLabel>
                <TextField
                  placeholder={
                    "NODE_ENV=production\nPORT=3000\nDATABASE_URL=postgres://..."
                  }
                  value={form.envVarsText}
                  onChange={(e) => setField("envVarsText", e.target.value)}
                  fullWidth
                  size="small"
                  multiline
                  rows={3}
                  helperText="One variable per line in KEY=VALUE format. Lines starting with # are ignored."
                  InputProps={{
                    sx: { fontFamily: "monospace", fontSize: "0.8rem" },
                  }}
                />
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* ── Actions ───────────────────────────────────────────────────────── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1,
        }}
      >
        <Button
          onClick={handleClose}
          sx={{ textTransform: "none", fontWeight: 600, mr: "auto" }}
        >
          Cancel
        </Button>
        {step === 1 && (
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={!isValid() || createLoading}
            disableElevation
            sx={{
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              minWidth: 100,
            }}
          >
            {createLoading ? "Creating..." : "Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateApplicationDialog;
